import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import moment from 'moment';
import { toast } from 'react-toastify';
import useHCPContext from './context/useHCPContext';
import Filter from './components/Filter';
import Table from './components/Table';
import useStore from '../../states/store';
import { getHCPReport, getHCPReportExport } from '../../api/hcpApi';

export default function HcpDashboard() {
  const auth = useStore((state) => state.auth);

  const defaultFilter = {
    department: { label: 'ALL', value: '' },
    team: { label: 'ALL', value: '' },
    manager_id: { label: 'ALL', value: '' },
    rep_id: { label: 'ALL', value: '' },
    account_type: { label: 'ALL', value: '' },
    date_filter_type: { label: 'Today', value: 'today' },
    date_from: moment().format('YYYY-MM-01'),
    date_to: moment().format('YYYY-MM-DD'),
  };

  const [filterForm, setFilterForm] = useState(defaultFilter);
  const [totalUnassigned, setTotalUnassigned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hcpData, setHcpData] = useState([]);

  const handleSearch = async (clear = null) => {
    try {
      setLoading(true);
      let request;
      if (clear === 'reset') {
        request = {
          department: defaultFilter.department.value,
          team: defaultFilter.team.value,
          manager_id: defaultFilter.manager_id.value,
          specialist: defaultFilter.rep_id.value,
          account_type: defaultFilter.account_type.value,
          date_filter_type: defaultFilter?.date_filter_type?.value,
          date_from: defaultFilter?.date_from,
          date_to: defaultFilter?.date_to,
        };
      } else {
        request = {
          department: filterForm.department.value,
          team: filterForm.team.value,
          manager_id: filterForm.manager_id.value,
          specialist: filterForm.rep_id.value,
          account_type: filterForm.account_type.value,
          date_filter_type: filterForm?.date_filter_type?.value,
          date_from: filterForm?.date_from,
          date_to: filterForm?.date_to,
        };
      }

      const response = await getHCPReport(request);
      const { code, data } = response.data;
      if (code === '200') {
        const newData = data.map((row) => ({
          id: row.id,
          manager: row.manager,
          rep_name: row.rep_name,
          call_reach_protect_t: row.call_reach_protect_t,
          call_reach_protect_a: row.call_reach_protect_a,
          call_reach_protect_percent: `${row.call_reach_protect_percent.toFixed(2)}%`,
          call_reach_develop_t: row.call_reach_develop_t,
          call_reach_develop_a: row.call_reach_develop_a,
          call_reach_develop_percent: `${row.call_reach_develop_percent.toFixed(2)}%`,
          call_reach_gain_t: row.call_reach_gain_t,
          call_reach_gain_a: row.call_reach_gain_a,
          call_reach_gain_percent: `${row.call_reach_gain_percent.toFixed(2)}%`,
          call_reach_total_t: row.call_reach_total_t,
          call_reach_total_a: row.call_reach_total_a,
          call_reach_total_percent: `${row.call_reach_total_percent.toFixed(2)}%`,

          call_volume_protect_t: row.call_volume_protect_t,
          call_volume_protect_a: row.call_volume_protect_a,
          call_volume_protect_percent: `${row.call_volume_protect_percent.toFixed(2)}%`,
          call_volume_develop_t: row.call_volume_develop_t,
          call_volume_develop_a: row.call_volume_develop_a,
          call_volume_develop_percent: `${row.call_volume_develop_percent.toFixed(2)}%`,
          call_volume_gain_t: row.call_volume_gain_t,
          call_volume_gain_a: row.call_volume_gain_a,
          call_volume_gain_percent: `${row.call_volume_gain_percent.toFixed(2)}%`,
          call_volume_total_t: row.call_volume_total_t,
          call_volume_total_a: row.call_volume_total_a,
          call_volume_total_percent: `${row.call_volume_total_percent.toFixed(2)}%`,

          call_concentration_protect_t: row.call_concentration_protect_t,
          call_concentration_protect_a: row.call_concentration_protect_a,
          call_concentration_protect_percent: `${row.call_concentration_protect_percent.toFixed(
            2,
          )}%`,
          call_concentration_develop_t: row.call_concentration_develop_t,
          call_concentration_develop_a: row.call_concentration_develop_a,
          call_concentration_develop_percent: `${row.call_concentration_develop_percent.toFixed(
            2,
          )}%`,
          call_concentration_gain_t: row.call_concentration_gain_t,
          call_concentration_gain_a: row.call_concentration_gain_a,
          call_concentration_gain_percent: `${row.call_concentration_gain_percent.toFixed(2)}%`,
          call_concentration_total_t: row.call_concentration_total_t,
          call_concentration_total_a: row.call_concentration_total_a,
          call_concentration_total_percent: `${row.call_concentration_total_percent.toFixed(2)}%`,
        }));
        setHcpData(newData);
        setTotalUnassigned(response?.data?.total_unassigned ?? 0);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleHCPExport = async () => {
    try {
      toast.loading('Please wait ...');

      const request = {
        department: filterForm.department.value,
        team: filterForm.team.value,
        manager_id: filterForm.manager_id.value,
        specialist: filterForm.rep_id.value,
        account_type: filterForm.account_type.value,
        date_filter_type: filterForm?.date_filter_type?.value,
        date_from: filterForm?.date_from,
        date_to: filterForm?.date_to,
      };

      const res = await getHCPReportExport(request);

      const { code, result, message } = res.data.response;
      if (code !== '200') {
        throw Object.assign(new Error(message), { code: 402 });
      }
      toast.dismiss();

      window.location.href = result;
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const handleClear = () => {
    setFilterForm(defaultFilter);
    handleSearch('reset');
  };

  useState(() => {
    handleSearch();
  }, [loading]);
  return (
    <useHCPContext.Provider
      value={useMemo(
        () => ({
          auth,
          filterForm,
          loading,
          hcpData,
          totalUnassigned,
          setFilterForm,
          handleSearch,
          handleClear,
          handleHCPExport,
        }),
        [loading, filterForm, hcpData],
      )}
    >
      <div className='body-content'>
        <Box sx={{ backgroundColor: '#fff', boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Box>
            <Box>
              <Grid container spacing={2}>
                <Grid xs={12} sm={12} md={12}>
                  <Box sx={{ backgroundColor: '#fff' }} mt={4} p={2}>
                    <Table />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </div>
    </useHCPContext.Provider>
  );
}
